import {
  MeetingSessionConfiguration,
  NScaleVideoUplinkBandwidthPolicy,
  VideoCodecCapability,
  ConsoleLogger,
  VideoPriorityBasedPolicy,
} from "amazon-chime-sdk-js";
import { DeviceLabels } from "amazon-chime-sdk-component-library-react";
import { MeetingMode } from "../../video.types";
import { VIDEO_QUALITY, SDK_LOG_LEVELS } from "../../video.constants";
import type { JoinInfo } from "@core/features/videoMeeting";

export type JoinChimeSessionOptions = {
  joinInfo: JoinInfo;
  enableSimulcast: boolean;
  priorityBasedPolicy?: VideoPriorityBasedPolicy;
  keepLastFrameWhenPaused: boolean;
  meetingManager: any;
  setRegion: (region: string) => void;
  setMeetingMode: (mode: MeetingMode) => void;
};

const logger = new ConsoleLogger("PollinatorVideo", SDK_LOG_LEVELS.warn);

export async function joinChimeSession({
  joinInfo,
  enableSimulcast,
  priorityBasedPolicy,
  keepLastFrameWhenPaused,
  meetingManager,
  setRegion,
  setMeetingMode,
}: JoinChimeSessionOptions): Promise<void> {
  const meetingSessionConfig = new MeetingSessionConfiguration(
    joinInfo.Meeting,
    joinInfo.Attendee
  );

  setRegion(joinInfo.Meeting.MediaRegion);

  // Configure session
  meetingSessionConfig.enableSimulcastForUnifiedPlanChromiumBasedBrowsers =
    enableSimulcast;
  if (priorityBasedPolicy) {
    meetingSessionConfig.videoDownlinkBandwidthPolicy = priorityBasedPolicy;
  }
  meetingSessionConfig.keepLastFrameWhenPaused = keepLastFrameWhenPaused;

  // Configure uplink bandwidth policy (skip when simulcast is on — SDK uses
  // DefaultSimulcastUplinkPolicy internally and the two would conflict)
  if (!enableSimulcast) {
    const selfAttendeeId =
      joinInfo.Attendee?.AttendeeId || "unknown";
    meetingSessionConfig.videoUplinkBandwidthPolicy =
      new NScaleVideoUplinkBandwidthPolicy(selfAttendeeId, true, logger);
  }

  // Join (makes the audioVideo facade available)
  try {
    await meetingManager.join(meetingSessionConfig, {
      deviceLabels: DeviceLabels.None,
      enableWebAudio: true,
      skipDeviceSelection: true,
    });
  } catch (err) {
    console.error("[joinChimeSession] meetingManager.join() failed:", err);
    throw err;
  }

  // Configure video quality and codec preferences BEFORE start() so the
  // initial negotiation uses the correct parameters (avoids renegotiation
  // flicker when the user first enables their camera)
  if (meetingManager.meetingSession?.audioVideo) {
    const av = meetingManager.meetingSession.audioVideo;

    av.chooseVideoInputQuality(
      VIDEO_QUALITY.width,
      VIDEO_QUALITY.height,
      VIDEO_QUALITY.frameRate
    );

    // Only cap bandwidth manually when simulcast is off — simulcast manages
    // layers and bitrates automatically via the SFU
    if (!enableSimulcast) {
      av.setVideoMaxBandwidthKbps(VIDEO_QUALITY.maxBandwidthKbps);
    }

    // Prefer VP9 (better compression at same quality) with VP8 fallback
    av.setVideoCodecSendPreferences([
      VideoCodecCapability.vp9(),
      VideoCodecCapability.vp8(),
    ]);
  }

  // Start the session
  try {
    await meetingManager.start();
  } catch (err) {
    console.error("[joinChimeSession] meetingManager.start() failed:", err);
    throw err;
  }

  setMeetingMode(MeetingMode.Attendee);
}
